import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { extractErrorMessage, reportFormError } from '../utils/errorHandling';

const LoginPage: React.FC = () => {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, error, clearError } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!identifier || !password) {
      const missingFields: Array<{ field: string; message: string }> = [];
      if (!identifier) {
        missingFields.push({
          field: 'identifier',
          message: 'Email or phone number is required',
        });
      }
      if (!password) {
        missingFields.push({
          field: 'password',
          message: 'Password is required',
        });
      }

      const message =
        missingFields.length === 2
          ? 'Please enter both email/phone and password'
          : missingFields[0].message;
      toast.error(message);
      reportFormError({
        formName: 'Login',
        message,
        category: 'validation',
        validationErrors: missingFields,
        tags: ['auth'],
        userEmail: identifier,
      });
      return;
    }

    setIsLoading(true);
    clearError();

    try {
      await login({ identifier, password });
      toast.success('Login successful!');
    } catch (err) {
      console.error('Login failed:', err);
      const message = extractErrorMessage(
        err,
        'Login failed. Please check your credentials and try again.',
      );
      toast.error(message);
      reportFormError({
        formName: 'Login',
        error: err,
        message,
        tags: ['auth'],
        userEmail: identifier,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-brand-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <img 
              src="/img/logo.png" 
              alt="Trigon Logo" 
              className="h-16 w-20 object-contain"
            />
          </div>
          <h2 className="text-3xl font-bold text-gradient mb-2">
            Welcome Back
          </h2>
          <p className="text-sm text-primary-600 font-medium">
            Staff Portal • HR, Managers, and Administrators
          </p>
        </div>
        
        <div className="card">
          <div className="card-body">
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="identifier" className="form-label">
                  Email Address or Phone Number
                </label>
                <input
                  id="identifier"
                  name="identifier"
                  type="text"
                  autoComplete="username"
                  required
                  className="form-input"
                  placeholder="Enter your email address or phone number"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  disabled={isLoading}
                />
              </div>
              
              <div>
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="form-input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              {error && (
                <div className="rounded-lg bg-error-50 border border-error-200 p-4">
                  <div className="text-sm text-error-700 font-medium">
                    {error}
                  </div>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary w-full justify-center"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Signing in...
                    </div>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-8">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-primary-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-gradient-to-br from-primary-50 via-white to-brand-50 text-primary-600 font-medium">
                Need access?
              </span>
            </div>
          </div>
          
          <div className="mt-6 text-center">
            <p className="text-sm text-primary-600">
              Contact your administrator to get login credentials
            </p>
          </div>
        </div>

        <div className="text-center">
          <p className="text-sm text-primary-600">
            Are you a candidate? {' '}
            <a 
              href="/register" 
              className="font-semibold text-brand-600 hover:text-brand-700 transition-colors"
            >
              Register here
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
